/**
 * LncRNAProfileViz - Comprehensive lncRNA profile visualization.
 *
 * Renders all precomputed data for a single lncRNA gene in a three-tab layout:
 *   Overview · Disease Appearances · Literature
 *
 * Used when the user asks "tell me everything about [gene]" and the
 * get_lncrna_comprehensive_profile orchestrator tool returns data.
 */

import React, { useState, useCallback } from 'react';
import { NetworkGraphViz } from './NetworkGraphViz';
import { API_BASE_URL } from '../../../services/chatApi';
import InfoTooltip from './InfoTooltip';

// -------------------------------------------------------------------------
// Types
// -------------------------------------------------------------------------

interface AppearanceEntry {
  disease: string;
  dataset_id: string;
  cell_type: string;
  rank?: number;
  log2fc: number;
  adj_pvalue?: number;
  disease_mean?: number;
  normal_mean?: number;
  regulation_direction: string;
  has_grn?: boolean;
  grn_dataset_id?: string;
}

interface Paper {
  pmid: string;
  title: string;
  abstract?: string;
  year?: string;
  authors?: string;
  journal?: string;
  is_retracted?: boolean;
}

interface OtherGRNPath {
  disease: string;
  dataset_id: string;
  cell_type: string;
  rank?: number;
  enrichment_score: number;
  composite_score: number;
}

interface ProfileSummary {
  total_appearances: number;
  diseases_present: string[];
  top_disease: string;
  top_cell_type: string;
  top_dataset_id: string;
  best_score: number;
  best_rank?: number;
  best_log2fc: number;
  regulation_direction: string;
}

interface LncRNAProfile {
  lncrna: string;
  precomputed_at?: string;
  summary: ProfileSummary;
  appearances: AppearanceEntry[];
  best_grn?: any;
  knockdown_results: any[];
  literature: { papers: Paper[]; gemini_summary: string; search_terms_used?: string[] };
  other_grn_paths: OtherGRNPath[];
}

interface LncRNAProfileVizProps {
  data: {
    type: 'lncrna_profile';
    profile: LncRNAProfile;
    from_precomputed: boolean;
  };
  onAction?: (action: string, params?: any) => void;
}

type Tab = 'overview' | 'appearances' | 'literature';

// -------------------------------------------------------------------------
// Helper utilities
// -------------------------------------------------------------------------

const downloadBlob = (content: string, filename: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const downloadGRNJson = (grn: any, lncrna: string, disease: string) => {
  const filename = `${lncrna}_${disease.replace(/\s+/g, '_')}_GRN.json`;
  downloadBlob(JSON.stringify(grn, null, 2), filename, 'application/json');
};

const downloadEdgesCSV = (grn: any, lncrna: string, disease: string) => {
  const edges =
    (grn?.disease_grn?.edges ?? grn?.edges ?? []) as any[];
  const header = 'source,target,weight,type\n';
  const rows = edges
    .map((e: any) => `${e.source ?? ''},${e.target ?? ''},${e.weight ?? e.score ?? ''},${e.type ?? ''}`)
    .join('\n');
  const filename = `${lncrna}_${disease.replace(/\s+/g, '_')}_GRN_edges.csv`;
  downloadBlob(header + rows, filename, 'text/csv');
};

const regulationBadge = (dir: string) => {
  const up = dir?.toLowerCase().includes('up');
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
        up ? 'bg-red-900/30 text-red-400' : 'bg-blue-900/30 text-blue-400'
      }`}
    >
      {up ? '↑ Upregulated' : '↓ Downregulated'}
    </span>
  );
};

// -------------------------------------------------------------------------
// Sub-components
// -------------------------------------------------------------------------

const TabButton: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
  badge?: number;
}> = ({ label, active, onClick, badge }) => (
  <button
    onClick={onClick}
    className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
      active
        ? 'border-blue-500 text-blue-400'
        : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600'
    }`}
  >
    {label}
    {badge != null && badge > 0 && (
      <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-gray-800 text-gray-400 rounded-full">
        {badge}
      </span>
    )}
  </button>
);

// Overview tab
const OverviewTab: React.FC<{ profile: LncRNAProfile }> = ({ profile }) => {
  const { summary, literature } = profile;

  return (
    <div className="space-y-4">
      {/* Summary cards — 3 cards (knockdown rescue removed) */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-gray-800 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-blue-400">{summary.total_appearances}</div>
          <div className="text-xs text-gray-500 mt-1">Disease contexts</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-purple-400">{summary.diseases_present.length}</div>
          <div className="text-xs text-gray-500 mt-1">Unique diseases</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-amber-400">{literature.papers.length}</div>
          <div className="text-xs text-gray-500 mt-1">Publications</div>
        </div>
      </div>

      {/* Best context */}
      <div className="border border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-gray-300">Best disease context</h4>
          {regulationBadge(summary.regulation_direction)}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-gray-500">Disease: </span>
            <span className="font-medium">{summary.top_disease || '—'}</span>
          </div>
          <div>
            <span className="text-gray-500">Cell type: </span>
            <span className="font-medium">{summary.top_cell_type || '—'}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-500">Rank: </span>
            <span className="font-medium">#{summary.best_rank ?? '—'}</span>
            <InfoTooltip title="Best Rank" content="Position among all dysregulated lncRNAs in this disease dataset. Rank 1 = most disease-specific." />
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-500">Log2FC: </span>
            <span className="font-medium">{summary.best_log2fc?.toFixed(2) ?? '—'}</span>
            <InfoTooltip title="Log2 Fold-Change" content="Log2(disease expression / normal expression). Positive = upregulated in disease. Negative = downregulated." />
          </div>
        </div>
      </div>

      {/* Literature section */}
      <div className="border border-gray-700 rounded-lg p-4 space-y-3">
        <h4 className="text-sm font-semibold text-gray-300">Literature summary</h4>

        {literature.gemini_summary ? (
          <div className="bg-blue-900/30 border border-blue-800 rounded-lg p-3">
            <p className="text-sm text-blue-200 leading-relaxed">{literature.gemini_summary}</p>
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic">No AI summary available.</p>
        )}

        {literature.papers.filter(p => !p.is_retracted).length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Recent publications ({literature.papers.filter(p => !p.is_retracted).length})
            </p>
            {literature.papers.filter(p => !p.is_retracted).slice(0, 5).map((paper) => (
              <div key={paper.pmid} className="text-sm leading-snug">
                <a
                  href={`https://pubmed.ncbi.nlm.nih.gov/${paper.pmid}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:underline"
                >
                  {paper.title}
                </a>
                {paper.year && (
                  <span className="text-xs text-gray-400 ml-1">({paper.year})</span>
                )}
              </div>
            ))}
            {literature.papers.filter(p => !p.is_retracted).length > 5 && (
              <p className="text-xs text-gray-400">
                +{literature.papers.filter(p => !p.is_retracted).length - 5} more in Literature tab
              </p>
            )}
          </div>
        )}

        {literature.papers.length === 0 && !literature.gemini_summary && (
          <p className="text-sm text-gray-400">No publications found.</p>
        )}
      </div>

      {/* Diseases list */}
      {summary.diseases_present.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-2">Disease coverage</h4>
          <div className="flex flex-wrap gap-2">
            {summary.diseases_present.map((d) => (
              <span
                key={d}
                className={`px-2 py-0.5 rounded text-xs ${
                  d === summary.top_disease
                    ? 'bg-blue-900/30 text-blue-400 font-medium'
                    : 'bg-gray-800 text-gray-400'
                }`}
              >
                {d}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Disease Appearances tab
const AppearancesTab: React.FC<{
  appearances: AppearanceEntry[];
  lncrna: string;
  onViewGRN: (app: AppearanceEntry) => void;
  onKnockdown: (app: AppearanceEntry) => void;
  loadingGRN?: string;
  expandedGRN?: { key: string; data: any } | null;
}> = ({ appearances, lncrna, onViewGRN, onKnockdown, loadingGRN, expandedGRN }) => {
  if (!appearances.length) {
    return <p className="text-sm text-gray-500">No appearances data available.</p>;
  }

  // Only drop entries where both expression values are completely absent (no data at all).
  // The backend already filters by significance, so low-but-valid expression values must be kept.
  const filtered = appearances.filter((a) =>
    a.disease_mean != null || a.normal_mean != null
  );

  // Sort by rank ascending (best rank first), null ranks at end
  const sorted = [...filtered].sort((a, b) => {
    if (a.rank == null && b.rank == null) return 0;
    if (a.rank == null) return 1;
    if (b.rank == null) return -1;
    return a.rank - b.rank;
  });

  return (
    <div className="overflow-x-auto w-full">
      <table className="w-full text-sm" style={{ minWidth: '700px' }}>
        <thead>
          <tr className="border-b border-gray-700">
            <th className="text-left py-2 pr-3 text-gray-400 font-medium">Disease</th>
            <th className="text-left py-2 pr-3 text-gray-400 font-medium">Cell type</th>
            <th className="text-right py-2 pr-3 text-gray-400 font-medium">
              <span className="flex items-center justify-end gap-1">
                Log2FC
                <InfoTooltip title="Log2 Fold-Change" content="Log2(disease mean / normal mean expression). Positive = upregulated. Negative = downregulated." position="right" />
              </span>
            </th>
            <th className="text-right py-2 pr-3 text-gray-400 font-medium" title="Disease mean / Normal mean expression">Expr D/N</th>
            <th className="text-right py-2 text-gray-400 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((app, i) => {
            const key = `${app.disease}||${app.dataset_id}||${app.cell_type}`;
            const isExpanded = expandedGRN?.key === key;
            const isLoading = loadingGRN === key;

            return (
              <React.Fragment key={key}>
                <tr className={`border-b border-gray-700 ${i === 0 ? 'bg-blue-900/20' : ''}`}>
                  <td className="py-2 pr-3">
                    {i === 0 && (
                      <span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-1.5 align-middle" />
                    )}
                    {app.disease}
                  </td>
                  <td className="py-2 pr-3 text-gray-400">{app.cell_type || '—'}</td>
                  <td className="py-2 pr-3 text-right">
                    <span className={app.log2fc >= 0 ? 'text-red-400' : 'text-blue-400'}>
                      {app.log2fc >= 0 ? '↑' : '↓'}{Math.abs(app.log2fc).toFixed(2)}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-right text-gray-500 text-xs">
                    {app.disease_mean != null
                      ? `${app.disease_mean.toFixed(3)} / ${(app.normal_mean ?? 0).toFixed(3)}`
                      : '—'}
                  </td>
                  <td className="py-2 text-right">
                    <div className="flex gap-1 justify-end">
                      <button
                        onClick={() => onViewGRN(app)}
                        disabled={isLoading}
                        className="text-xs px-2 py-1 rounded border border-blue-700 text-blue-400 hover:bg-blue-900/30 disabled:opacity-50"
                        title={app.has_grn ? 'Load precomputed GRN' : 'Compute GRN on demand (~30s)'}
                      >
                        {isLoading
                          ? (app.has_grn ? 'Loading…' : 'Computing…')
                          : isExpanded ? 'Hide GRN' : 'View GRN'}
                      </button>
                      <button
                        onClick={() => onKnockdown(app)}
                        className="text-xs px-2 py-1 rounded border border-purple-700 text-purple-400 hover:bg-purple-900/30"
                      >
                        Knockdown
                      </button>
                    </div>
                  </td>
                </tr>
                {isExpanded && expandedGRN?.data && (
                  <tr>
                    <td colSpan={6} className="pb-4 pt-2">
                      <div className="border border-gray-700 rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2 bg-gray-800 border-b border-gray-700">
                          <span className="text-xs font-medium text-gray-400">
                            GRN — {app.disease} / {app.cell_type}
                          </span>
                          <div className="flex gap-2">
                            <button
                              onClick={() => downloadGRNJson(expandedGRN.data, lncrna, app.disease)}
                              className="text-xs px-2 py-1 rounded border border-gray-600 text-gray-400 hover:bg-gray-700"
                            >
                              ↓ JSON
                            </button>
                            <button
                              onClick={() => downloadEdgesCSV(expandedGRN.data, lncrna, app.disease)}
                              className="text-xs px-2 py-1 rounded border border-gray-600 text-gray-400 hover:bg-gray-700"
                            >
                              ↓ CSV
                            </button>
                          </div>
                        </div>
                        <NetworkGraphViz
                          data={expandedGRN.data}
                          interactive={false}
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {sorted.length > 0 && (
        <p className="text-xs text-gray-400 mt-2">
          Sorted by rank · ● Best rank highlighted in blue
        </p>
      )}
    </div>
  );
};

// Literature tab
const LiteratureTab: React.FC<{ literature: LncRNAProfile['literature'] }> = ({ literature }) => {
  const [expandedPmid, setExpandedPmid] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      {literature.gemini_summary && (
        <div className="bg-blue-900/30 border border-blue-800 rounded-lg p-4">
          <div className="flex items-center gap-1.5 mb-1">
          <h4 className="text-sm font-semibold text-blue-300">AI Summary</h4>
          <InfoTooltip title="AI Summary" content="Generated by Gemini from PubMed abstracts. Summarizes known biology, disease associations, and therapeutic relevance based on published literature." />
        </div>
          <p className="text-sm text-blue-200 leading-relaxed">{literature.gemini_summary}</p>
        </div>
      )}

      {literature.papers.length === 0 ? (
        <p className="text-sm text-gray-400">No publications found.</p>
      ) : (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-300">
            Publications ({literature.papers.filter(p => !p.is_retracted).length})
          </h4>
          {literature.papers
            .filter(p => !p.is_retracted)
            .map((paper) => (
            <div key={paper.pmid} className="border border-gray-700 rounded-lg p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <a
                    href={`https://pubmed.ncbi.nlm.nih.gov/${paper.pmid}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-gray-200 hover:text-blue-400 leading-snug block"
                  >
                    {paper.title}
                  </a>
                  <p className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-1 items-center">
                    {paper.authors && <span>{paper.authors}</span>}
                    {paper.authors && (paper.journal || paper.year) && <span>·</span>}
                    {paper.journal && <span className="italic">{paper.journal}</span>}
                    {paper.journal && paper.year && <span>·</span>}
                    {paper.year && <span>{paper.year}</span>}
                    {(paper.authors || paper.journal || paper.year) && <span>·</span>}
                    <a
                      href={`https://pubmed.ncbi.nlm.nih.gov/${paper.pmid}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:underline"
                    >
                      PMID:{paper.pmid}
                    </a>
                  </p>
                </div>
                {paper.abstract && paper.abstract !== 'No abstract available' && (
                  <button
                    onClick={() =>
                      setExpandedPmid(expandedPmid === paper.pmid ? null : paper.pmid)
                    }
                    className="text-xs text-gray-500 hover:text-gray-300 flex-shrink-0 mt-0.5"
                  >
                    {expandedPmid === paper.pmid ? 'Less ▲' : 'Abstract ▼'}
                  </button>
                )}
              </div>
              {expandedPmid === paper.pmid && paper.abstract && (
                <p className="text-xs text-gray-400 mt-2 leading-relaxed border-t border-gray-700 pt-2">
                  {paper.abstract}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------------------------------
// Main component
// -------------------------------------------------------------------------

export const LncRNAProfileViz: React.FC<LncRNAProfileVizProps> = ({ data, onAction }) => {
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [loadingGRNKey, setLoadingGRNKey] = useState<string | null>(null);
  const [expandedGRN, setExpandedGRN] = useState<{ key: string; data: any } | null>(null);
  const [grnError, setGrnError] = useState<string | null>(null);

  const lncrnaSymbol = data?.profile?.lncrna ?? '';

  const handleViewGRN = useCallback(async (app: AppearanceEntry) => {
    if (!lncrnaSymbol) return;  // guard: no valid gene symbol

    const key = `${app.disease}||${app.dataset_id}||${app.cell_type}`;

    // Toggle off if same row clicked again
    if (expandedGRN?.key === key) {
      setExpandedGRN(null);
      return;
    }

    setLoadingGRNKey(key);
    setExpandedGRN(null);
    setGrnError(null);

    try {
      // Use grn_dataset_id if available — it may differ from dataset_id when the
      // GRN was precomputed for a different dataset than the expression-stats scan.
      const params = new URLSearchParams({
        disease: app.disease,
        dataset_id: app.grn_dataset_id ?? app.dataset_id
      });
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180_000); // 3 min
      const resp = await fetch(
        `${API_BASE_URL}/api/v1/lncrna-profile/${encodeURIComponent(lncrnaSymbol)}/grn?${params}`,
        { signal: controller.signal }
      );
      clearTimeout(timeoutId);
      if (!resp.ok) throw new Error(`GRN fetch failed: ${resp.status}`);
      const json = await resp.json();
      setExpandedGRN({ key, data: json.grn_data });
    } catch (err: any) {
      console.error('Failed to load GRN:', err);
      setGrnError(
        err?.name === 'AbortError'
          ? 'GRN computation timed out (>3 min). Try a different disease context.'
          : 'Failed to load GRN. Check console for details.'
      );
    } finally {
      setLoadingGRNKey(null);
    }
  }, [lncrnaSymbol, expandedGRN]);

  const handleKnockdown = useCallback((app: AppearanceEntry) => {
    if (onAction) {
      onAction('simulate_knockdown', {
        gene: lncrnaSymbol,
        disease: app.disease,
        cell_type: app.cell_type,
        dataset_id: app.dataset_id,
      });
    }
  }, [lncrnaSymbol, onAction]);

  if (!data?.profile) return null;

  const { profile } = data;
  const { summary, appearances, literature } = profile;

  return (
    <div className="origin-card mt-4 overflow-hidden">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-gray-700">
        <div className="flex items-center gap-3">
          <div>
            <h3 className="text-base font-bold text-gray-100">{profile.lncrna}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Comprehensive lncRNA profile ·{' '}
              <span className="text-blue-400">{summary.total_appearances} disease contexts</span>
              {' · '}
              {regulationBadge(summary.regulation_direction)}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex overflow-x-auto border-b border-gray-700 px-4 scrollbar-none">
        <TabButton label="Overview" active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} />
        <TabButton
          label="Disease Appearances"
          active={activeTab === 'appearances'}
          onClick={() => setActiveTab('appearances')}
          badge={summary.total_appearances}
        />
        <TabButton
          label="Literature"
          active={activeTab === 'literature'}
          onClick={() => setActiveTab('literature')}
          badge={literature.papers.length}
        />
      </div>

      {/* Tab content */}
      <div className="p-4">
        {activeTab === 'overview' && <OverviewTab profile={profile} />}

        {activeTab === 'appearances' && (
          <>
            {grnError && (
              <div className="mb-3 px-3 py-2 rounded bg-red-900/30 border border-red-800 text-xs text-red-400">
                {grnError}
              </div>
            )}
            <AppearancesTab
              appearances={appearances}
              lncrna={profile.lncrna}
              onViewGRN={handleViewGRN}
              onKnockdown={handleKnockdown}
              loadingGRN={loadingGRNKey ?? undefined}
              expandedGRN={expandedGRN}
            />
          </>
        )}

        {activeTab === 'literature' && <LiteratureTab literature={literature} />}
      </div>
    </div>
  );
};

export default LncRNAProfileViz;
